"use client";

import { useSearchParams } from "next/navigation";
import { useState } from "react";

import { getSourceEmail } from "@/components/auth/auth-form-wrapper";
import { CredentialsHeader } from "@/components/auth/auth-header";
import { AuthModeSwitch } from "@/components/auth/auth-mode-switch";
import { formMode } from "@/components/auth/form-mode";
import LoginForm from "@/components/auth/login-form.container";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AUTH_AUDIENCES } from "@/lib/constants/auth-audiences";

const DIALOG_TITLES = {
  [formMode.signin]: "Welcome back",
  [formMode.signup]: "Get started",
} as const;

interface AuthDialogProps {
  mode: formMode;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface AuthDialogContentProps {
  initialMode: formMode;
  defaultEmail: string | undefined;
  onSuccess: () => void;
}

function AuthDialogContent({
  initialMode,
  defaultEmail,
  onSuccess,
}: AuthDialogContentProps) {
  const [mode, setMode] = useState(initialMode);

  return (
    <>
      <DialogTitle className="sr-only">{DIALOG_TITLES[mode]}</DialogTitle>
      <div className="px-5 py-5 sm:px-6 sm:py-6">
        <LoginForm
          mode={mode}
          audience={AUTH_AUDIENCES.JOB_SEEKER}
          defaultEmail={defaultEmail}
          onSuccess={onSuccess}
          header={<CredentialsHeader title={DIALOG_TITLES[mode]} />}
          footer={<AuthModeSwitch mode={mode} query="" onSwitch={setMode} />}
        />
      </div>
    </>
  );
}

export default function AuthDialog({
  mode: initialMode,
  isOpen,
  onClose,
  onSuccess,
}: AuthDialogProps): React.JSX.Element {
  const searchParams = useSearchParams();
  const defaultEmail = getSourceEmail(searchParams);

  const handleSuccess = () => {
    onClose();
    onSuccess?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-[340px] sm:max-w-[400px] p-0 gap-0 bg-card border-border shadow-2xl overflow-hidden rounded-xl"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        {isOpen && (
          <AuthDialogContent
            key={initialMode}
            initialMode={initialMode}
            defaultEmail={defaultEmail}
            onSuccess={handleSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
